import React, { useEffect, useState } from 'react';
import { requestAPI, dispatchAPI, branchAPI } from '../../services/api';
import Badge from '../../components/common/Badge';

const fmtErr = (d, fallback) => {
  if (!d) return fallback;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map(e => e?.msg ? `${(e.loc || []).slice(-1)[0] || 'field'}: ${e.msg}` : JSON.stringify(e)).join('; ');
  if (typeof d === 'object') return d.msg || d.message || JSON.stringify(d);
  return String(d);
};

const PRIORITY_COLOR = {
  CRITICAL: '#ef4444', HIGH: '#f97316', NORMAL: '#94335a', LOW: '#a99aa0',
};

function ScoreBar({ score, label, max = 100 }) {
  const pct = Math.min((score / max) * 100, 100);
  const color = pct >= 70 ? '#10b981' : pct >= 40 ? '#f59e0b' : '#ef4444';
  return (
    <div style={{ marginBottom: 4 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
        <span style={{ fontSize: 10, color: '#8b7681' }}>{label}</span>
        <span style={{ fontSize: 10, fontWeight: 700, color }}>{Math.round(score)}</span>
      </div>
      <div style={{ background: '#f1f5f9', borderRadius: 10, height: 5, overflow: 'hidden' }}>
        <div style={{ height: '100%', background: color, width: `${pct}%`, borderRadius: 10, transition: 'width 0.4s' }} />
      </div>
    </div>
  );
}

export default function Dispatch() {
  const [requests, setRequests] = useState([]);
  const [nurses, setNurses] = useState([]);
  const [selected, setSelected] = useState(null);
  const [selectedNurse, setSelectedNurse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nurseLoading, setNurseLoading] = useState(false);
  const [dispatching, setDispatching] = useState(false);
  const [toast, setToast] = useState(null);
  const [dispatchLog, setDispatchLog] = useState([]);
  const [logLoading, setLogLoading] = useState(false);
  const [poolDiag, setPoolDiag] = useState(null);
  // Branch scoping: empty string = whole workforce.
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState('');

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchRequests = () => {
    requestAPI.list({ status: 'REQUESTED', limit: 100 })
      .then(r => setRequests(r.data || []))
      .catch(() => setRequests([]))
      .finally(() => setLoading(false));
  };

  const fetchLog = () => {
    setLogLoading(true);
    dispatchAPI.logs({ limit: 20 })
      .then(r => setDispatchLog(r.data || []))
      .catch(() => setDispatchLog([]))
      .finally(() => setLogLoading(false));
  };

  useEffect(() => {
    fetchRequests();
    fetchLog();
    branchAPI.list().then(r => setBranches(r.data || [])).catch(() => setBranches([]));
  }, []);

  const handleSelectRequest = (req, scopeBranchId = branchId) => {
    setSelected(req);
    setSelectedNurse(null);
    setNurses([]);
    setNurseLoading(true);
    dispatchAPI.findNurses({
      service_type: req.service_type,
      lat: req.location_lat,
      lng: req.location_lng,
      // Lets the backend match the request's required_qualifications too
      // (skills component becomes a coverage ratio).
      request_id: req.request_id,
      ...(scopeBranchId ? { branch_id: scopeBranchId } : {}),
      limit: 10,
    })
      .then(r => {
        setNurses(r.data || []);
        setPoolDiag(r.meta?.diagnostics || null);
      })
      .catch(() => { setNurses([]); setPoolDiag(null); })
      .finally(() => setNurseLoading(false));
  };

  const handleBranchChange = (e) => {
    const id = e.target.value;
    setBranchId(id);
    if (selected) handleSelectRequest(selected, id);
  };

  const handleDispatch = async () => {
    if (!selected || !selectedNurse) return;
    setDispatching(true);
    try {
      await dispatchAPI.assign(selected.request_id, { nurse_id: selectedNurse.nurse_id });
      showToast(`✅ Nurse assigned to request successfully`);
      setSelected(null);
      setSelectedNurse(null);
      setNurses([]);
      fetchRequests();
      fetchLog();
    } catch (err) {
      showToast('❌ ' + fmtErr(err.response?.data?.detail, 'Dispatch failed'), 'error');
    } finally {
      setDispatching(false);
    }
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: '#2b1b22', margin: 0 }}>Dispatch Engine</h1>
          <p style={{ color: '#8b7681', fontSize: 13, marginTop: 4 }}>
            Uber-like nurse matching · Distance + Rating + Specialization scoring
          </p>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          {branches.length > 0 && (
            <select value={branchId} onChange={handleBranchChange} aria-label="Scope to branch" style={{
              padding: '7px 12px', borderRadius: 8, border: '1px solid #ece3e7',
              fontSize: 13, color: '#5e4a53', background: '#fff', fontWeight: 600, cursor: 'pointer',
            }}>
              <option value="">All branches</option>
              {branches.map(b => (
                <option key={b.branch_id} value={b.branch_id}>{b.name}</option>
              ))}
            </select>
          )}
          <div style={{ padding: '6px 16px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 20, fontSize: 12, color: '#16a34a', fontWeight: 600 }}>
            {requests.length} Pending
          </div>
          <button onClick={() => { fetchRequests(); fetchLog(); }} style={{
            padding: '8px 16px', background: '#f8fafc', border: '1px solid #ece3e7',
            borderRadius: 8, fontSize: 13, color: '#5e4a53', cursor: 'pointer', fontWeight: 600,
          }}>
            ↺ Refresh
          </button>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: 24, right: 24, zIndex: 9999,
          padding: '12px 20px', borderRadius: 10, fontSize: 14, fontWeight: 600,
          background: toast.type === 'error' ? '#fee2e2' : '#d1fae5',
          color: toast.type === 'error' ? '#b91c1c' : '#065f46',
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
        }}>
          {toast.msg}
        </div>
      )}

      {/* Score algorithm explanation */}
      <div style={{
        background: 'linear-gradient(135deg,#2b1b22,#1e3a5f)', borderRadius: 12,
        padding: '14px 20px', marginBottom: 20, display: 'flex', gap: 20, flexWrap: 'wrap',
      }}>
        <div style={{ color: '#a99aa0', fontSize: 12, fontWeight: 600, marginRight: 4 }}>SCORING ALGORITHM (100 pts):</div>
        {[
          { label: 'Distance', pts: 40, color: '#94335a' },
          { label: 'Rating', pts: 25, color: '#f59e0b' },
          { label: 'Specialization', pts: 20, color: '#10b981' },
          { label: 'Experience', pts: 15, color: '#8b5cf6' },
          { label: 'Track Record', pts: 10, color: '#06b6d4' },
        ].map(({ label, pts, color }) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: color }} />
            <span style={{ fontSize: 12, color: '#ece3e7' }}>{label} <strong style={{ color }}>{pts}pts</strong></span>
          </div>
        ))}
      </div>

      {/* Main 3-panel layout */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr 0.8fr', gap: 16, marginBottom: 20 }}>
        {/* Panel 1: Pending Requests */}
        <div style={{ background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
          <h3 style={{ margin: '0 0 14px', fontSize: 14, fontWeight: 700, color: '#2b1b22' }}>
            Pending Requests
            <span style={{ marginLeft: 8, background: '#fee2e2', color: '#dc2626', borderRadius: 20, padding: '2px 8px', fontSize: 11 }}>
              {requests.length}
            </span>
          </h3>
          {loading ? (
            <div style={{ color: '#a99aa0', fontSize: 13 }}>Loading requests...</div>
          ) : requests.length === 0 ? (
            <div style={{ color: '#a99aa0', textAlign: 'center', padding: '30px 0', fontSize: 13 }}>
              🎉 No pending requests
            </div>
          ) : (
            <div style={{ maxHeight: 480, overflowY: 'auto' }}>
              {requests.map(r => (
                <div
                  key={r.request_id}
                  onClick={() => handleSelectRequest(r)}
                  style={{
                    padding: '11px 12px',
                    border: `2px solid ${selected?.request_id === r.request_id ? '#94335a' : '#f1f5f9'}`,
                    borderRadius: 8, marginBottom: 6, cursor: 'pointer',
                    background: selected?.request_id === r.request_id ? '#fdeef5' : '#fafafa',
                    transition: 'all 0.12s',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#2b1b22' }}>
                      {r.patient_name || 'Patient'}
                    </span>
                    <span style={{
                      fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 10,
                      background: `${PRIORITY_COLOR[r.priority] || '#a99aa0'}20`,
                      color: PRIORITY_COLOR[r.priority] || '#a99aa0',
                    }}>
                      {r.priority}
                    </span>
                  </div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#94335a', marginBottom: 2 }}>
                    {r.service_type?.replace(/_/g, ' ')}
                  </div>
                  <div style={{ fontSize: 11, color: '#8b7681', marginBottom: 3 }}>
                    📍 {r.location_address || `${r.location_lat?.toFixed(3)}, ${r.location_lng?.toFixed(3)}`}
                  </div>
                  <div style={{ fontSize: 10, color: '#a99aa0' }}>
                    {r.preferred_date ? new Date(r.preferred_date).toLocaleString() : 'ASAP'}
                    {r.visit_frequency && r.visit_frequency !== 'ONE_OFF' && (
                      <> · 🔁 {r.visit_frequency.replace(/_/g, ' ').toLowerCase()}
                        {r.end_date ? ` until ${r.end_date}` : ''}</>
                    )}
                  </div>
                  {(r.required_qualifications || []).length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                      {r.required_qualifications.map(q => (
                        <span key={q} style={{
                          fontSize: 9.5, fontWeight: 700, padding: '1px 6px', borderRadius: 8,
                          background: '#ede9fe', color: '#5b21b6',
                        }}>
                          {q.replace(/_/g, ' ')}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Panel 2: Nurse Candidates */}
        <div style={{ background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
          <h3 style={{ margin: '0 0 14px', fontSize: 14, fontWeight: 700, color: '#2b1b22' }}>
            Nurse Candidates
            {nurses.length > 0 && (
              <span style={{ marginLeft: 8, background: '#d1fae5', color: '#065f46', borderRadius: 20, padding: '2px 8px', fontSize: 11 }}>
                {nurses.length} found
              </span>
            )}
          </h3>

          {!selected ? (
            <div style={{ color: '#a99aa0', textAlign: 'center', padding: '40px 20px', fontSize: 13 }}>
              ← Select a care request to find matching nurses
            </div>
          ) : nurseLoading ? (
            <div style={{ color: '#a99aa0', fontSize: 13 }}>Finding best nurses...</div>
          ) : nurses.length === 0 ? (
            <div style={{ color: '#8b7681', textAlign: 'center', padding: '30px 16px', fontSize: 13, lineHeight: 1.6 }}>
              <div style={{ fontWeight: 700, color: '#5e4a53', marginBottom: 6 }}>
                {poolDiag?.reason || 'No nurse is currently available for this request.'}
              </div>
              {poolDiag && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: 14, flexWrap: 'wrap', marginTop: 8 }}>
                  {[
                    ['Registered', poolDiag.nurses_total],
                    ['Available', poolDiag.nurses_available],
                    ['On visits', poolDiag.nurses_on_visit],
                  ].map(([label, n]) => (
                    <span key={label} style={{ fontSize: 11, background: '#f6eff2', borderRadius: 12, padding: '4px 10px' }}>
                      {label}: <strong>{n}</strong>
                    </span>
                  ))}
                </div>
              )}
              <span style={{ display: 'block', fontSize: 12, color: '#a99aa0', marginTop: 10 }}>
                Nurses on active visits return to the pool automatically when their
                visit completes or is cancelled.
              </span>
            </div>
          ) : (
            <div style={{ maxHeight: 480, overflowY: 'auto' }}>
              {nurses.map((n, i) => (
                <div
                  key={n.nurse_id || i}
                  onClick={() => setSelectedNurse(n)}
                  style={{
                    padding: '12px 14px',
                    border: `2px solid ${selectedNurse?.nurse_id === n.nurse_id ? '#10b981' : '#f1f5f9'}`,
                    borderRadius: 8, marginBottom: 8, cursor: 'pointer',
                    background: selectedNurse?.nurse_id === n.nurse_id ? '#f0fdf4' : '#fafafa',
                    transition: 'all 0.12s',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#2b1b22' }}>
                        {i === 0 && <span style={{ marginRight: 4 }}>🏆</span>}
                        {n.name || `Nurse #${i + 1}`}
                      </div>
                      <div style={{ fontSize: 10, color: '#8b7681' }}>{n.license_number}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{
                        fontSize: 18, fontWeight: 800,
                        color: n.score >= 70 ? '#10b981' : n.score >= 40 ? '#f59e0b' : '#ef4444',
                      }}>
                        {n.score?.toFixed(0) ?? '—'}
                      </div>
                      <div style={{ fontSize: 9, color: '#a99aa0' }}>/ 100</div>
                    </div>
                  </div>

                  {/* Score breakdown */}
                  <div style={{ marginBottom: 6 }}>
                    <ScoreBar score={n.score_distance ?? 0} label="Distance" max={40} />
                    <ScoreBar score={n.score_rating ?? 0} label="Rating" max={25} />
                    <ScoreBar score={n.score_specialization ?? 0} label={n.required_qualifications?.length ? 'Skills match' : 'Specialization'} max={20} />
                    <ScoreBar score={n.score_experience ?? 0} label="Experience" max={15} />
                  </div>

                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 10, padding: '2px 6px', background: '#fdeef5', color: '#0369a1', borderRadius: 10 }}>
                      ⭐ {n.rating ? Number(n.rating).toFixed(1) : '—'}
                    </span>
                    <span style={{ fontSize: 10, padding: '2px 6px', background: '#f0fdf4', color: '#15803d', borderRadius: 10 }}>
                      📍 {n.distance_km != null ? `${n.distance_km.toFixed(1)} km` : '—'}
                    </span>
                    <span style={{
                      fontSize: 10, padding: '2px 6px', borderRadius: 10,
                      background: (n.open_visits_today ?? 0) >= 3 ? '#fee2e2' : (n.open_visits_today ?? 0) >= 1 ? '#fef9c3' : '#f0fdf4',
                      color: (n.open_visits_today ?? 0) >= 3 ? '#991b1b' : (n.open_visits_today ?? 0) >= 1 ? '#92400e' : '#15803d',
                    }}>
                      🗓 {n.open_visits_today ?? 0} today
                      {(n.score_workload_penalty ?? 0) > 0 && ` · −${Math.round(n.score_workload_penalty)} pts`}
                    </span>
                    {n.specialization?.slice(0, 2).map(s => (
                      <span key={s} style={{ fontSize: 10, padding: '2px 6px', background: '#faf5ff', color: '#7e22ce', borderRadius: 10 }}>
                        {s}
                      </span>
                    ))}
                  </div>

                  {/* Required-skill coverage: ✓ held, ✗ missing — the reason
                      behind the Skills match bar, not a separate metric. */}
                  {(n.required_qualifications || []).length > 0 && (
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 6 }}>
                      {n.required_qualifications.map(q => {
                        const has = (n.matched_qualifications || []).includes(q);
                        return (
                          <span key={q} style={{
                            fontSize: 9.5, fontWeight: 700, padding: '1px 6px', borderRadius: 8,
                            background: has ? '#d1fae5' : '#fee2e2',
                            color: has ? '#065f46' : '#991b1b',
                          }}>
                            {has ? '✓' : '✗'} {q.replace(/_/g, ' ')}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Panel 3: Assignment Summary */}
        <div style={{ background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
          <h3 style={{ margin: '0 0 14px', fontSize: 14, fontWeight: 700, color: '#2b1b22' }}>Assignment</h3>

          {!selected || !selectedNurse ? (
            <div style={{ color: '#a99aa0', fontSize: 12, textAlign: 'center', padding: '30px 10px' }}>
              Select a request and nurse to assign
            </div>
          ) : (
            <div>
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, color: '#8b7681', marginBottom: 4 }}>PATIENT</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#2b1b22' }}>{selected.patient_name || 'Patient'}</div>
                <div style={{ fontSize: 12, color: '#94335a', fontWeight: 600, marginTop: 2 }}>{selected.service_type?.replace(/_/g, ' ')}</div>
                <div style={{ fontSize: 11, color: '#8b7681', marginTop: 2 }}>📍 {selected.location_address}</div>
                {selected.visit_frequency && selected.visit_frequency !== 'ONE_OFF' && (
                  <div style={{ fontSize: 11, color: '#8b7681', marginTop: 2 }}>
                    🔁 {selected.visit_frequency.replace(/_/g, ' ').toLowerCase()}
                    {selected.end_date ? ` until ${selected.end_date}` : ''}
                  </div>
                )}
                {(selected.required_qualifications || []).length > 0 && (
                  <div style={{ fontSize: 11, color: '#5b21b6', marginTop: 2 }}>
                    🎓 {selected.required_qualifications.map(q => q.replace(/_/g, ' ')).join(', ')}
                  </div>
                )}
              </div>
              <div style={{ width: '100%', height: 1, background: '#f1f5f9', margin: '10px 0' }} />
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, color: '#8b7681', marginBottom: 4 }}>NURSE</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#2b1b22' }}>{selectedNurse.name}</div>
                <div style={{ fontSize: 11, color: '#8b7681' }}>Score: {selectedNurse.score?.toFixed(0)} / 100</div>
              </div>
              <button
                onClick={handleDispatch}
                disabled={dispatching}
                style={{
                  width: '100%', padding: '11px', background: dispatching ? '#a99aa0' : '#10b981',
                  color: '#fff', border: 'none', borderRadius: 8,
                  fontSize: 13, fontWeight: 700, cursor: dispatching ? 'not-allowed' : 'pointer',
                  marginTop: 8,
                }}
              >
                {dispatching ? 'Dispatching...' : '🚀 Dispatch Now'}
              </button>
            </div>
          )}

          {/* Recent dispatch log */}
          <div style={{ marginTop: 20 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#8b7681', marginBottom: 8 }}>RECENT DISPATCHES</div>
            {logLoading ? (
              <div style={{ fontSize: 11, color: '#a99aa0' }}>Loading...</div>
            ) : dispatchLog.length === 0 ? (
              <div style={{ fontSize: 11, color: '#a99aa0' }}>No dispatch history yet</div>
            ) : (
              dispatchLog.slice(0, 8).map((log, i) => (
                <div key={i} style={{
                  padding: '6px 0', borderBottom: '1px solid #f8fafc',
                  fontSize: 11, color: '#475569',
                }}>
                  <div style={{ fontWeight: 600 }}>{log.service_type?.replace(/_/g, ' ') || 'Dispatch'}</div>
                  <div style={{ color: '#a99aa0', fontSize: 10 }}>
                    {log.created_at ? new Date(log.created_at).toLocaleString() : '—'}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
